import type { Kernel, MathShim, Params, RNG } from "../sdk/kernel";

interface OptionPartial {
  /** Sum of discounted payoffs across the paths in this chunk. */
  payoffSum: number;
  paths: number;
}

// European call option on a synthetic underlying priced by Monte Carlo under
// geometric Brownian motion (GBM). Synthetic curves only — no market data — consistent
// with the gallery scoping (2026-06-12). Each iteration simulates one terminal price
// S_T = S0 · exp((r − σ²/2)·T + σ·√T·Z), Z ~ N(0,1) via Box-Muller, and accumulates the
// discounted call payoff e^{−rT}·max(S_T − K, 0).
//
// The model inputs (spot, strike, rate, vol, expiry) are exposed as params so anyone can
// price against their own assumptions; the result still reproduces bitwise for any fixed
// (seed, N, params). Box-Muller pulls log/cos/exp through the fdlibm WASM shim, so this is
// cross-engine bitwise (verifiable: true).
const TWO_PI = 6.283185307179586;

export const options: Kernel<OptionPartial> = {
  id: "options",
  label: "Options pricing (GBM)",
  // verifiable: true — M3 active. Box-Muller's log/cos/exp go through the fdlibm WASM shim,
  // which is bitwise-identical across V8/SpiderMonkey/JSC (Spike 03), so this kernel now
  // reproduces cross-engine. (sqrt is IEEE-bitwise and stays native.)
  verifiable: true,
  blurb: "Prices a European call on a synthetic GBM underlying by Monte Carlo. Tune spot, strike, rate, volatility and expiry to price your own scenario.",
  resultUnit: "call price",
  defaultN: 500_000,
  chunkSize: 10_000, // ~50 chunks at default N

  params: [
    { key: "s0", label: "Spot price", default: 100, min: 1, max: 1000, step: 1, unit: "$", hint: "Current price of the underlying (S₀)." },
    { key: "k", label: "Strike", default: 105, min: 1, max: 1000, step: 1, unit: "$", hint: "Exercise price of the call (K)." },
    { key: "r", label: "Risk-free rate", default: 3, min: -5, max: 25, step: 0.1, unit: "%", hint: "Annualised risk-free interest rate." },
    { key: "sigma", label: "Volatility", default: 20, min: 1, max: 150, step: 0.5, unit: "%", hint: "Annualised volatility of the underlying (σ)." },
    { key: "t", label: "Time to expiry", default: 1, min: 0.05, max: 10, step: 0.05, unit: "yr", hint: "Years until the option expires (T)." },
  ],

  chunk(count: number, rng: RNG, math: MathShim, params: Params): OptionPartial {
    const S0 = params.s0!;
    const K = params.k!;
    const R = params.r! / 100; // percent → rate
    const SIGMA = params.sigma! / 100; // percent → rate
    const T = params.t!;
    const drift = (R - (SIGMA * SIGMA) / 2) * T;
    const vol = SIGMA * math.sqrt(T);
    const discount = math.exp(-R * T);
    let payoffSum = 0;
    for (let p = 0; p < count; p++) {
      // Box-Muller: one standard normal per path. u1 nudged off 0 so log is finite.
      const u1 = rng.float();
      const u2 = rng.float();
      const z = math.sqrt(-2 * math.log(u1 > 0 ? u1 : 1e-12)) * math.cos(TWO_PI * u2);
      const sT = S0 * math.exp(drift + vol * z);
      const payoff = sT > K ? sT - K : 0;
      payoffSum += discount * payoff;
    }
    return { payoffSum, paths: count };
  },

  reduce(partials: OptionPartial[]) {
    let payoffSum = 0;
    let paths = 0;
    const samples: number[] = [];
    for (const p of partials) {
      payoffSum += p.payoffSum;
      paths += p.paths;
      samples.push(p.paths > 0 ? p.payoffSum / p.paths : 0); // per-chunk price estimate
    }
    // Final result: the Monte Carlo call price (mean discounted payoff).
    return { result: paths > 0 ? payoffSum / paths : 0, samples };
  },
};
